import { Box, Stack, alpha, keyframes, styled } from '@mui/material';

export type ArenaTone = 'success' | 'error' | 'info' | 'warning' | 'nudge';

/**
 * One accent per tone. The plate itself stays dark for every message — a
 * full-bleed red or blue block (what notistack ships) reads as a system error
 * from the browser, not as something the match said.
 */
export const ARENA_TONES: Record<
  ArenaTone,
  { accent: string; glow: string; label: string }
> = {
  success: { accent: '#46C93A', glow: 'rgba(70,201,58,.32)', label: 'Nice' },
  error: { accent: '#FF5D5D', glow: 'rgba(255,93,93,.32)', label: 'Hold on' },
  info: { accent: '#7FB6E8', glow: 'rgba(127,182,232,.30)', label: 'Arena' },
  warning: {
    accent: '#F5A524',
    glow: 'rgba(245,165,36,.32)',
    label: 'Heads up',
  },
  nudge: { accent: '#FFC85A', glow: 'rgba(255,200,90,.40)', label: 'Nudge' },
};

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-14px) scale(.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const sheen = keyframes`
  from { transform: translateX(-140%) skewX(-18deg); }
  to   { transform: translateX(320%) skewX(-18deg); }
`;

const glyphPulse = keyframes`
  0%, 100% { transform: scale(1); }
  45%      { transform: scale(1.14); }
`;

const drain = keyframes`
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
`;

export const ArenaPlate = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: ArenaTone }>(({ theme, tone }) => {
  const { accent, glow } = ARENA_TONES[tone];
  return {
    position: 'relative',
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minWidth: 288,
    maxWidth: 420,
    padding: '12px 12px 12px 16px',
    borderRadius: 16,
    background:
      'linear-gradient(135deg, #12345C 0%, #0C2444 55%, #081A33 100%)',
    border: '1px solid rgba(255,255,255,.14)',
    boxShadow: `0 16px 40px rgba(4,14,30,.55), 0 0 0 1px ${glow} inset`,
    animation: `${slideIn} .32s cubic-bezier(.2,.9,.3,1) both`,
    // The accent lives on the edge rather than the fill, so five tones can
    // share one surface without any of them shouting.
    '&::before': {
      content: '""',
      position: 'absolute',
      insetBlock: 0,
      left: 0,
      width: 4,
      background: accent,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      insetBlock: 0,
      width: 70,
      pointerEvents: 'none',
      background:
        'linear-gradient(90deg, transparent, rgba(255,255,255,.14), transparent)',
      animation: `${sheen} 1.1s .22s ease-out both`,
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      '&::after': { animation: 'none', opacity: 0 },
    },
    [theme.breakpoints.down('sm')]: { minWidth: 0, width: '100%' },
  };
});

export const ArenaGlyph = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: ArenaTone }>(({ tone }) => {
  const { accent } = ARENA_TONES[tone];
  return {
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    width: 34,
    height: 34,
    borderRadius: 12,
    color: accent,
    background: alpha(accent, 0.18),
    border: `1px solid ${alpha(accent, 0.45)}`,
    ...(tone === 'nudge' && {
      animation: `${glyphPulse} 1.5s ease-in-out infinite`,
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }),
  };
});

export const ArenaBody = styled(Box)({ minWidth: 0, flex: 1 });

export const ArenaLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: ArenaTone }>(({ theme, tone }) => ({
  fontFamily: theme.palette.guidedCourse.fontMono,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  color: ARENA_TONES[tone].accent,
}));

export const ArenaMessage = styled(Box)(({ theme }) => ({
  marginTop: 2,
  fontFamily: theme.palette.guidedCourse.fontBody,
  fontSize: 13.5,
  fontWeight: 700,
  lineHeight: 1.35,
  color: '#fff',
  wordBreak: 'break-word',
}));

export const ArenaAction = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  padding: '7px 13px',
  borderRadius: 999,
  cursor: 'pointer',
  userSelect: 'none',
  fontFamily: theme.palette.guidedCourse.fontBody,
  fontSize: 12,
  fontWeight: 800,
  color: '#fff',
  background: 'rgba(255,255,255,.12)',
  border: '1px solid rgba(255,255,255,.2)',
  '&:hover': { background: 'rgba(255,255,255,.22)' },
  '&:focus-visible': { outline: '3px solid #FFE27A', outlineOffset: 2 },
}));

export const ArenaClose = styled(Box)({
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: '50%',
  cursor: 'pointer',
  color: 'rgba(255,255,255,.6)',
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,.12)' },
  '&:focus-visible': { outline: '3px solid #FFE27A', outlineOffset: 2 },
});

/** How long is left before it leaves — otherwise a toast vanishes mid-read. */
export const ArenaTimer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'duration',
})<{ tone: ArenaTone; duration: number }>(({ tone, duration }) => ({
  position: 'absolute',
  insetInline: 0,
  bottom: 0,
  height: 2,
  transformOrigin: 'left',
  background: ARENA_TONES[tone].accent,
  opacity: 0.6,
  animation: `${drain} ${duration}ms linear both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));
